import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";

/**
 * Hook to determine if the current user has any NEW pending requests
 * that require their action (connection requests OR mentorship requests).
 * 
 * Returns boolean `hasNew` instead of a count.
 * 
 * For mentors: checks pending connection requests AND pending mentorship requests
 * For entrepreneurs: checks pending connection requests only (they don't action mentorship requests)
 */
export function useNewConnectionsIndicator() {
  const { user } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();

  return useQuery({
    queryKey: ["new-connections-indicator", user?.id, profile?.role],
    queryFn: async () => {
      if (!user?.id) return { hasNew: false };

      const isMentor = profile?.role === "mentor";

      // Check for pending connection requests where user is the recipient
      // This applies to ALL users (entrepreneurs AND mentors can receive requests)
      const { count: pendingConnectionCount, error: connError } = await supabase
        .from("connections")
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", user.id)
        .eq("status", "pending");

      if (connError) {
        console.error("Error checking pending connections:", connError);
      }

      let pendingMentorshipCount = 0;

      // For mentors, also check pending mentorship requests addressed to them
      if (isMentor) {
        const { count, error: mentorshipError } = await supabase
          .from("mentorship_requests")
          .select("*", { count: "exact", head: true })
          .eq("mentor_id", user.id)
          .eq("status", "pending");

        if (mentorshipError) {
          console.error("Error checking pending mentorship requests:", mentorshipError);
        } else {
          pendingMentorshipCount = count || 0;
        }
      }

      const hasNew = (pendingConnectionCount || 0) > 0 || pendingMentorshipCount > 0;

      return { hasNew };
    },
    enabled: !!user?.id && !!profile && !profileLoading,
  });
}
